import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorBox,
  Input,
  Label,
} from "@gearbox-protocol/deploy-tools-ui";
import { chains, getNetworkType } from "@gearbox-protocol/sdk/onchain";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { useAccount } from "wagmi";

import { useUnpausedForkStatus } from "../context";
import { useMyAssets } from "./MyAssetsProvider";
import useDegenNFTFaucet from "./useDegenNFTFaucet";
import useFaucetClaim from "./useFaucetClaim";

const ActionsCard: FC = () => {
  const { address: account } = useAccount();
  const { originChainId } = useUnpausedForkStatus();
  const { native } = useMyAssets();
  const [{ loading: degenLoading, value: degenBalance }, requestDegenNFT] =
    useDegenNFTFaucet();
  const { loading: claimLoading, error: claimError, claim } = useFaucetClaim();

  const [amountUSD, setAmountUSD] = useState("");
  const [midas, setMidas] = useState(false);
  const [nativeAmount, setNativeAmount] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const nativeToken = useMemo(() => {
    return chains[getNetworkType(originChainId)].nativeCurrency.symbol;
  }, [originChainId]);

  const disabled = !account;

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border p-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-start gap-2">
          <div className="flex flex-col flex-1 gap-2">
            <Input
              className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              type="number"
              placeholder="USD amount (optional)"
              value={amountUSD}
              disabled={disabled}
              onChange={e => setAmountUSD(e.target.value)}
            />

            <div className="flex flex-row items-center gap-2">
              <Checkbox
                id="faucet-midas"
                checked={midas}
                disabled={disabled}
                onCheckedChange={checked => setMidas(checked === true)}
              />
              <Label htmlFor="faucet-midas">Greenlist Midas investor</Label>
            </div>
          </div>

          <Button
            variant="accent"
            disabled={disabled}
            loading={claimLoading}
            onClick={() =>
              claim({ usdAmount: amountUSD.trim() || undefined, midas })
            }
          >
            Request tokens
          </Button>
        </div>

        <span className="text-xs text-muted-foreground">
          Sends both {nativeToken} and faucet tokens to your connected wallet
          address
        </span>
      </div>

      {!!claimError && <ErrorBox error={claimError} />}

      <div className="flex flex-col border-t border-border pt-4">
        <Button
          variant="outline"
          className="w-full"
          disabled={disabled}
          loading={native.requesting}
          onClick={() => setDialogOpen(true)}
        >
          Request {nativeToken}
        </Button>
      </div>

      <div className="flex flex-row items-center justify-between gap-2 border-t border-border pt-3">
        <span className="text-xs text-muted-foreground">
          Degen NFT balance: {degenBalance?.toString() ?? "?"}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          loading={degenLoading}
          onClick={requestDegenNFT}
        >
          Request Degen NFT
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Request {nativeToken}</DialogTitle>
          </DialogHeader>
          <Input
            type="number"
            placeholder={`Enter ${nativeToken} amount without decimals (optional)`}
            value={nativeAmount}
            onChange={e => setNativeAmount(e.target.value)}
          />
          <DialogFooter>
            <Button
              variant="accent"
              onClick={() => {
                native.requestEth(nativeAmount);
                setDialogOpen(false);
                setNativeAmount("");
              }}
            >
              Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActionsCard;
